import * as React from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { AlertTriangle, ArrowRight, Loader2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convexApi";
import { BADGE_TONES } from "@/lib/badge-tones";
import { formatCurrency, formatRelativeTime } from "@/lib/format";
import { SalesRepPerformance } from "./SalesRepPerformance";

interface DashboardTabProps {
  authUserId?: string;
  user?: { name?: string; email?: string } | null;
}

const timePresets = [
  { label: "7d", value: "7" },
  { label: "30d", value: "30" },
  { label: "90d", value: "90" },
  { label: "All", value: "all" },
] as const;

const coffeeFuturesChartConfig = {
  price: {
    label: "Price",
    color: "var(--chart-1)",
  },
} satisfies ChartConfig;

interface MarketPriceRow {
  id: string;
  marketPrice: number;
  marketPriceFormatted: string;
  marketPriceDelta: number | null;
  effectiveDate: number;
  formattedDate: string;
  relativeDate: string;
  updatedBy: string;
  notes: string;
  searchIndex: string;
}

export function DashboardTab({ authUserId, user }: DashboardTabProps) {
  // ── Convex Queries ──
  const stats = useQuery(api.utils.getDatabaseStats, authUserId ? {} : "skip");
  const currentMarketPrice = useQuery(
    api.admin.getCurrentMarketPrice,
    authUserId ? { authUserId } : "skip",
  );
  const priceHistory = useQuery(
    api.admin.getMarketPriceHistory,
    authUserId ? { authUserId, limit: 150 } : "skip",
  );
  const coffeeFuturesHistory = useQuery(
    api.coffeeFutures.getCoffeeFuturesHistory,
    authUserId ? { limit: 48 } : "skip",
  );
  const latestCoffeeFutures = useQuery(
    api.coffeeFutures.getLatestCoffeeFuturesPrice,
    authUserId ? {} : "skip",
  );
  const overdueData = useQuery(
    api.aging.getPaymentStatusReport,
    authUserId ? {} : "skip",
  );

  // ── Convex Mutations / Actions ──
  const updateMarketPriceMut = useMutation(api.admin.updateMarketPrice);
  const fetchCoffeeFuturesAction = useAction(api.coffeeFutures.fetchCoffeeFuturesPrice);

  // ── Local State ──
  const [newPrice, setNewPrice] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [timePreset, setTimePreset] = React.useState<string>("30");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [page, setPage] = React.useState(1);
  const pageSize = 12;

  const [isFetchingFutures, setIsFetchingFutures] = React.useState(false);
  const [futuresError, setFuturesError] = React.useState<string | null>(null);
  const [futuresSuccess, setFuturesSuccess] = React.useState(false);

  const [updateError, setUpdateError] = React.useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = React.useState(false);
  const [isUpdatingPrice, setIsUpdatingPrice] = React.useState(false);

  // ── Derived Values ──
  const isLoadingStats = stats === undefined;
  const isLoadingPricing = currentMarketPrice === undefined;
  const isLoadingCoffeeFutures = coffeeFuturesHistory === undefined;
  const isLoadingOverdue = overdueData === undefined;

  const marketPriceValue = currentMarketPrice?.marketPrice ?? 0;
  const formattedCurrentPrice = formatCurrency(marketPriceValue);

  // Overdue payments
  const overduePayments = React.useMemo(() => {
    if (!overdueData?.buckets) return [];
    return (overdueData.buckets as any[])
      .flatMap((bucket: any) => bucket.payments || [])
      .filter((p: any) => p.daysOverdue > 0);
  }, [overdueData]);
  const totalOverdueAmount = overdueData?.totalOverdue ?? 0;
  const totalOverdueCount = overdueData?.totalOverdueCount ?? 0;
  const criticalOverdue = overduePayments.filter(
    (p: any) => p.daysOverdue > 30,
  ).length;

  // Price History Rows
  const priceRows = React.useMemo((): MarketPriceRow[] => {
    if (!priceHistory || priceHistory.length === 0) return [];
    return (priceHistory as any[]).map((record: any, index: number, array: any[]) => {
      const effectiveDate = record.effectiveDate || record.createdAt || 0;
      const mp = record.marketPrice ?? 0;
      const previous = array[index + 1];
      const previousPrice = previous?.marketPrice;
      const delta = previousPrice !== undefined ? mp - previousPrice : null;
      const formattedDate = new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(effectiveDate));
      return {
        id: record._id ?? `${effectiveDate}-${index}`,
        marketPrice: mp,
        marketPriceFormatted: formatCurrency(mp),
        marketPriceDelta: delta,
        effectiveDate,
        formattedDate,
        relativeDate: formatRelativeTime(effectiveDate),
        updatedBy: record.updatedBy ?? "Unassigned",
        notes: record.notes ?? "",
        searchIndex: [formattedDate, record.updatedBy ?? "", record.notes ?? "", formatCurrency(mp)]
          .join(" ")
          .toLowerCase(),
      };
    });
  }, [priceHistory]);

  const filteredRows = React.useMemo((): MarketPriceRow[] => {
    let result = priceRows;
    if (timePreset !== "all") {
      const now = Date.now();
      const cutoff = now - Number(timePreset) * 24 * 60 * 60 * 1000;
      result = result.filter((row) => row.effectiveDate >= cutoff);
    }
    if (searchTerm.trim()) {
      const sv = searchTerm.trim().toLowerCase();
      result = result.filter((row) => row.searchIndex.includes(sv));
    }
    return result;
  }, [priceRows, timePreset, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = filteredRows.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  const startRow = filteredRows.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, filteredRows.length);

  // Reset page on filter change
  React.useEffect(() => {
    setPage(1);
  }, [timePreset, searchTerm]);

  // Coffee Futures Chart Data
  const coffeeFuturesChartData = React.useMemo(() => {
    if (!coffeeFuturesHistory || coffeeFuturesHistory.length === 0) return [];
    return [...(coffeeFuturesHistory as any[])]
      .reverse()
      .map((item: any) => ({
        timestamp: new Date(item.timestamp).getTime(),
        price: item.price / 100,
        label: `$${(item.price / 100).toFixed(2)}/lb`,
        date:
          item.date ||
          new Date(item.timestamp).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
      }));
  }, [coffeeFuturesHistory]);

  // ── Handlers ──
  async function handleUpdatePrice() {
    if (isUpdatingPrice) return;
    const priceInDollars = parseFloat(newPrice);
    if (Number.isNaN(priceInDollars) || priceInDollars <= 0) {
      setUpdateError("Please enter a valid price");
      return;
    }
    setUpdateError(null);
    setUpdateSuccess(false);
    setIsUpdatingPrice(true);
    try {
      await updateMarketPriceMut({
        authUserId: authUserId!,
        marketPrice: priceInDollars,
        notes: notes || undefined,
        updatedBy: user?.name || user?.email || "Unknown",
      });
      setUpdateSuccess(true);
      setNewPrice("");
      setNotes("");
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (err: any) {
      console.error("Failed to update market price:", err);
      setUpdateError("Failed to update market price");
    } finally {
      setIsUpdatingPrice(false);
    }
  }

  async function handleFetchCoffeeFutures() {
    setIsFetchingFutures(true);
    setFuturesError(null);
    setFuturesSuccess(false);
    try {
      const data = await fetchCoffeeFuturesAction({});
      if ((data as any).success) {
        setFuturesSuccess(true);
        setTimeout(() => setFuturesSuccess(false), 3000);
      } else {
        setFuturesError((data as any).error || "Failed to fetch coffee futures");
      }
    } catch (err: any) {
      console.error("Error fetching coffee futures:", err);
      setFuturesError("Failed to fetch coffee futures data");
    } finally {
      setIsFetchingFutures(false);
    }
  }

  function handleExportCsv() {
    if (filteredRows.length === 0) return;
    const header = ["Date", "Updated by", "Price", "Change", "Notes"];
    const csv = [
      header.join(","),
      ...filteredRows.map((row) =>
        [
          `"${row.formattedDate}"`,
          `"${row.updatedBy}"`,
          row.marketPrice.toFixed(2),
          row.marketPriceDelta ? row.marketPriceDelta.toFixed(2) : "0.00",
          `"${(row.notes || "").replace(/"/g, "'")}"`,
        ].join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "market-price-history.csv");
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-10">
      {/* Header */}
      <section className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Admin Overview
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">
          Operational control center
        </h1>
        <p className="max-w-2xl text-xs text-muted-foreground">
          Adjust market pricing, audit historical changes, and keep teams aligned from a single surface.
        </p>
      </section>

      {/* Overdue Payments Alert */}
      {isLoadingOverdue ? (
        <Skeleton className="h-24 w-full" />
      ) : totalOverdueCount > 0 ? (
        <section className="rounded-lg border-2 border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                <AlertTriangle className="size-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                  {totalOverdueCount} Overdue Payment{totalOverdueCount !== 1 ? "s" : ""}
                </p>
                <p className="text-xs text-red-600 dark:text-red-400">
                  Total: {formatCurrency(totalOverdueAmount)}
                  {criticalOverdue > 0 && (
                    <Badge variant="destructive" className="ml-2 text-xs">
                      {criticalOverdue} critical (30+ days)
                    </Badge>
                  )}
                </p>
              </div>
            </div>
            <Button variant="destructive" size="sm" className="gap-2" asChild>
              <Link to="/admin/accounting/aging">
                View Overdue
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </section>
      ) : null}

      {/* Market Price Management */}
      <section className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        {/* Left: Market Price + Coffee Futures */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xs font-semibold">Market price management</CardTitle>
            <CardDescription className="text-xs">
              Update the baseline rate powering contracts and inventory valuation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Current price
              </p>
              {isLoadingPricing ? (
                <Skeleton className="h-12 w-32" />
              ) : (
                <p className="text-4xl font-bold tracking-tight">{formattedCurrentPrice}</p>
              )}
            </div>

            <div className="space-y-3 rounded-md border bg-muted/30 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="new-price" className="text-xs font-medium uppercase tracking-wider">
                    New market price ($)
                  </Label>
                  <Input
                    id="new-price"
                    type="number"
                    step="0.01"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    placeholder="3.78"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="notes" className="text-xs font-medium uppercase tracking-wider">
                    Notes (optional)
                  </Label>
                  <Input
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add context..."
                    className="h-9"
                  />
                </div>
              </div>
              {updateError && <p className="text-xs text-destructive">{updateError}</p>}
              {updateSuccess && <p className="text-xs text-green-600">Market price updated successfully</p>}
              <Button onClick={handleUpdatePrice} disabled={isLoadingPricing || isUpdatingPrice} className="w-full">
                {isUpdatingPrice ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update market price"
                )}
              </Button>
            </div>

            {/* Coffee C Futures */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Coffee C Futures
                </p>
                {latestCoffeeFutures && (
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-xs font-semibold">
                      ${((latestCoffeeFutures as any).price / 100).toFixed(2)}/lb
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {(latestCoffeeFutures as any).date ||
                        new Date((latestCoffeeFutures as any).timestamp).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                    </span>
                  </div>
                )}
              </div>

              {isLoadingCoffeeFutures ? (
                <Skeleton className="h-48 w-full" />
              ) : coffeeFuturesChartData.length > 1 ? (
                <ChartContainer
                  config={coffeeFuturesChartConfig}
                  className="aspect-auto h-48 w-full"
                >
                  <LineChart
                    data={coffeeFuturesChartData}
                    margin={{ top: 8, right: 12, bottom: 0, left: -4 }}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={(v) => `$${v.toFixed(2)}`}
                      domain={["auto", "auto"]}
                    />
                    <ChartTooltip
                      cursor={false}
                      content={
                        <ChartTooltipContent
                          hideIndicator
                          formatter={(value) => (
                            <span className="font-mono font-medium text-foreground">
                              ${(value as number).toFixed(2)}/lb
                            </span>
                          )}
                        />
                      }
                    />
                    <Line
                      type="monotone"
                      dataKey="price"
                      stroke="var(--color-price)"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 0 }}
                    />
                  </LineChart>
                </ChartContainer>
              ) : coffeeFuturesChartData.length === 1 ? (
                <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-md border bg-muted/30 text-xs text-muted-foreground">
                  <p>Only one data point available</p>
                  <p className="text-xs">Click "Refresh" multiple times to collect more data</p>
                </div>
              ) : (
                <div className="flex h-48 items-center justify-center rounded-md border bg-muted/30 text-xs text-muted-foreground">
                  No futures data available
                </div>
              )}

              {futuresError && <p className="text-xs text-destructive">{futuresError}</p>}
              {futuresSuccess && (
                <p className="text-xs text-green-600">
                  Fetched Coffee C Futures: ${(((latestCoffeeFutures as any)?.price || 0) / 100).toFixed(2)} /lb
                </p>
              )}
              <Button
                onClick={handleFetchCoffeeFutures}
                variant="outline"
                size="sm"
                className="w-full"
                disabled={isFetchingFutures}
              >
                {isFetchingFutures ? "Fetching..." : "Refresh"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Right: Market Price History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xs font-semibold">Market price history</CardTitle>
            <CardDescription className="text-xs">
              Search, filter, and export every pricing adjustment for audit trails.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1 rounded border bg-background px-2 py-1 text-xs">
                {timePresets.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={timePreset === preset.value ? "default" : "ghost"}
                    size="sm"
                    className="h-7 rounded px-3 text-xs"
                    onClick={() => setTimePreset(preset.value)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search notes or user"
                className="h-9 w-full max-w-xs text-xs"
              />
              <div className="ml-auto flex items-center gap-2">
                <Button onClick={handleExportCsv} variant="default" size="sm">
                  Export CSV
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border">
              <div className="overflow-x-auto">
                <Table className="min-w-[640px] text-xs">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Date</TableHead>
                      <TableHead>Updated by</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="py-8 text-center text-xs text-muted-foreground">
                          No pricing history yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRows.map((row) => (
                        <TableRow key={row.id} className="hover:bg-muted/30">
                          <TableCell className="whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="font-medium">{row.formattedDate}</span>
                              <span className="text-xs text-muted-foreground">{row.relativeDate}</span>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{row.updatedBy || "—"}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{row.marketPriceFormatted}</span>
                              {row.marketPriceDelta !== null && (
                                <Badge
                                  variant="outline"
                                  className={
                                    row.marketPriceDelta > 0
                                      ? BADGE_TONES.emerald
                                      : row.marketPriceDelta < 0
                                        ? BADGE_TONES.rose
                                        : BADGE_TONES.muted
                                  }
                                >
                                  {row.marketPriceDelta > 0 ? "+" : ""}
                                  {row.marketPriceDelta?.toFixed(2)}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <span className="line-clamp-2">{row.notes || "—"}</span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-xs text-muted-foreground">
                <span>
                  {filteredRows.length === 0
                    ? "No entries"
                    : `Viewing ${startRow}–${endRow} of ${filteredRows.length}`}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="font-medium">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Database Stats */}
      {isLoadingStats ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-4 w-24 animate-pulse bg-muted" />
                <div className="mt-2 h-4 w-16 animate-pulse bg-muted" />
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : (stats as any)?.error ? (
        <Card>
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>{(stats as any).error}</CardDescription>
          </CardHeader>
        </Card>
      ) : stats ? (
        <section className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Users</CardTitle>
                <CardDescription>Total user profiles</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(stats as any).tableStats?.users ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Products</CardTitle>
                <CardDescription>Total products</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(stats as any).tableStats?.products ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Orders</CardTitle>
                <CardDescription>Total orders</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(stats as any).tableStats?.orders ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Contracts</CardTitle>
                <CardDescription>Total contracts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(stats as any).tableStats?.contracts ?? 0}</div>
              </CardContent>
            </Card>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Active Contracts</CardTitle>
                <CardDescription>Signed contracts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(stats as any).businessStats?.activeContracts ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Pending Orders</CardTitle>
                <CardDescription>Orders awaiting processing</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(stats as any).businessStats?.pendingOrders ?? 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Inventory</CardTitle>
                <CardDescription>Total bags available</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(stats as any).businessStats?.totalInventoryBags ?? 0}</div>
              </CardContent>
            </Card>
          </div>
        </section>
      ) : null}

      {/* Sales Rep Performance */}
      <section>
        <SalesRepPerformance authUserId={authUserId} />
      </section>
    </div>
  );
}
